/*
 * The embedding bridge: a page that frames MocTel Vox (a dispatch or CRM
 * app) drives the phone and hears about its calls through postMessage.
 * public/sdk/vox-embed.js is the host side; docs/embedding.md the contract.
 *
 * - Only the parent window, and only from an origin of the site's
 *   embed_origins, is heard; anything else is dropped without an answer.
 * - The host says "hello" first; the bridge then talks to that origin only
 *   (never "*"). A later hello (the parent navigated) moves it.
 * - Every command gets one "result" with the command's id, like the core.
 * - The host sees calls and the phone's status; never credentials,
 *   provisioning or settings.
 */
import type { CallInfo, CoreEvent, Json } from "../core/types";

export const PROTOCOL = 1;

export type Activation = "needed" | "activating" | "active";

export interface BridgeStatus extends Json {
  activation: Activation;
  /** the lines: registered when one is */
  line: "none" | "registering" | "registered" | "failed";
  dnd: boolean;
  version: string;
}

/** What the bridge may do in the app (state.ts, through main.tsx). */
export interface BridgeApp {
  status(): BridgeStatus;
  calls(): CallInfo[];
  dial(to: string, ref?: string): Promise<CoreEvent>;
  answer(call: string): Promise<CoreEvent>;
  hangup(call: string): Promise<CoreEvent>;
  hold(call: string, on: boolean): Promise<CoreEvent>;
  mute(call: string, on: boolean): Promise<CoreEvent>;
  dtmf(call: string, digits: string): Promise<CoreEvent>;
  transfer(call: string, to: string): Promise<CoreEvent>;
  dnd(enable: boolean): Promise<void>;
  activate(token: string): Promise<CoreEvent>;
  /** the caller's name in the user's contacts, if any */
  contactName(number: string): string | undefined;
}

export type Post = (msg: Json, origin: string) => void;

type Answer = { ok: true; data?: Json } | { ok: false; error: string; message?: string };

export class Bridge {
  private origin = "";
  private lastStatus = "";

  constructor(
    private allowed: readonly string[],
    private app: BridgeApp,
    private post: Post,
  ) {}

  /** the host's origin once it said hello */
  get host(): string {
    return this.origin;
  }

  /** a window "message" event; fromParent: event.source === window.parent */
  async onMessage(data: unknown, origin: string, fromParent: boolean): Promise<void> {
    if (!fromParent || !this.allowed.includes(origin)) return;
    if (!data || typeof data !== "object") return;
    const m = data as Json;
    if (m.mtvox !== PROTOCOL || typeof m.cmd !== "string") return;
    const id = typeof m.id === "number" ? m.id : 0;
    if (m.cmd === "hello") {
      this.origin = origin;
      this.lastStatus = "";
      this.reply(id, { ok: true, data: { protocol: PROTOCOL, status: this.app.status(), calls: this.app.calls().map((c) => this.call(c)) } });
      this.status();
      return;
    }
    if (origin !== this.origin) return; // hello first
    let a: Answer;
    try {
      a = await this.run(m);
    } catch (e) {
      a = { ok: false, error: "failed", message: e instanceof Error ? e.message : String(e) };
    }
    this.reply(id, a);
  }

  /** a core event: calls go to the host */
  onCoreEvent(e: CoreEvent): void {
    if (e.ev === "call.state") this.send("call.state", this.call(e as unknown as CallInfo));
  }

  /** the phone's status changed (or may have): sent when it differs */
  status(): void {
    const s = this.app.status();
    const key = JSON.stringify(s);
    if (key === this.lastStatus) return;
    if (this.send("status", s)) this.lastStatus = key;
  }

  // ------------------------------------------------------------------
  private async run(m: Json): Promise<Answer> {
    const call = (): string => {
      if (typeof m.call === "string" && m.call) return m.call;
      // no call given: the one ringing (answer) or the one up (others)
      const live = this.app.calls().filter((c) => c.state !== "ended");
      const pick = m.cmd === "call.answer" ? live.find((c) => c.state === "incoming") : live.find((c) => c.state !== "incoming" && !c.on_hold) ?? live[0];
      return pick?.call ?? "";
    };
    switch (m.cmd) {
      case "status":
        return { ok: true, data: this.app.status() };
      case "call.list":
        return { ok: true, data: { calls: this.app.calls().map((c) => this.call(c)) } };
      case "call.dial": {
        const to = typeof m.to === "string" ? m.to.replace(/[\s().-]/g, "") : "";
        if (!/^\+?[0-9*#]{1,32}$/.test(to)) return { ok: false, error: "invalid", message: "to" };
        if (m.ref !== undefined && typeof m.ref !== "string") return { ok: false, error: "invalid", message: "ref" };
        return result(await this.app.dial(to, m.ref as string | undefined));
      }
      case "call.answer":
        return result(await this.app.answer(call()));
      case "call.hangup":
        return result(await this.app.hangup(call()));
      case "call.hold":
        return result(await this.app.hold(call(), m.hold !== false));
      case "call.mute":
        return result(await this.app.mute(call(), m.mute !== false));
      case "call.dtmf":
        return result(await this.app.dtmf(call(), String(m.digits ?? "")));
      case "call.transfer": {
        const to = typeof m.to === "string" ? m.to.replace(/[\s().-]/g, "") : "";
        if (!/^\+?[0-9*#]{1,32}$/.test(to)) return { ok: false, error: "invalid", message: "to" };
        return result(await this.app.transfer(call(), to));
      }
      case "dnd":
        await this.app.dnd(m.enable !== false);
        this.status();
        return { ok: true };
      case "activate": {
        const token = typeof m.token === "string" ? m.token : "";
        if (!token) return { ok: false, error: "invalid", message: "token" };
        return result(await this.app.activate(token));
      }
      default:
        return { ok: false, error: "unknown_command" };
    }
  }

  /** what the host learns about a call */
  private call(c: CallInfo): Json {
    const number = numberOf(c.peer);
    const contact = this.app.contactName(number);
    const out: Json = {
      call: c.call,
      state: c.state,
      dir: c.dir,
      number,
      ...(c.peer_name ? { name: c.peer_name } : {}),
      ...(contact ? { contact } : {}),
      ...(c.ref ? { ref: c.ref } : {}),
      ...(c.headers ? { headers: c.headers } : {}),
      on_hold: !!c.on_hold,
      muted: !!c.muted,
      duration: c.duration ?? 0,
    };
    const e = c as Json;
    if (c.state === "ended" && e.code) out.code = e.code;
    if (c.state === "ended" && e.reason) out.reason = e.reason;
    return out;
  }

  private reply(id: number, a: Answer): void {
    this.send("result", { id, ...a });
  }

  private send(ev: string, fields: Json): boolean {
    if (!this.origin) return false;
    this.post({ mtvox: PROTOCOL, ev, ...fields }, this.origin);
    return true;
  }
}

function result(r: CoreEvent): Answer {
  if (r.ok) return r.data ? { ok: true, data: r.data as Json } : { ok: true };
  return { ok: false, error: String(r.error ?? "failed"), ...(r.message ? { message: String(r.message) } : {}) };
}

/** sip:4185551234@host -> 4185551234 */
export function numberOf(uri: string): string {
  const m = /^sips?:([^@;]+)/.exec(uri);
  return m ? decodeURIComponent(m[1]) : uri;
}
